/**
 * Quick Calculator Popover
 *
 * Main shell component for the Quick License Plate Calculator.
 * Command-palette style UI that displays:
 * - License plate input
 * - Vehicle summary
 * - BPM calculation
 * - VAT/Margin classification
 * - Max buy price calculator
 * - Risk flags
 * - Action buttons
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calculator } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTeamId } from '@/hooks/useTeamId';
import { useQuickCalculator } from '../QuickCalculatorContext';
import { PlateInput } from './PlateInput';
import { VehicleSummaryCard } from './VehicleSummaryCard';
import { BpmCard } from './BpmCard';
import { ClassificationCard } from './ClassificationCard';
import { MaxBuyPriceCard } from './MaxBuyPriceCard';
import { RiskFlagsCard, hasActiveRiskFlags } from './RiskFlagsCard';
import { CalculatorActions } from './CalculatorActions';
import { CalculatorErrorCard } from './CalculatorErrorCard';
import { copyCalculatorResult } from './copyCalculatorResult';
import { buildCalculatorPrefill } from '../lib/buildCalculatorPrefill';
import { buildPrefillUrl } from '../lib/prefillCodec';
import { lookupExistingVehicleWithTimeout } from '../lib/lookupExistingVehicle';

export function QuickCalculatorPopover() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const teamId = useTeamId();
  const {
    state,
    retryCountdown,
    close,
    setPlate,
    setExpectedSellingPrice,
    setTargetMarginPct,
    calculate,
    setExistingVehicle,
  } = useQuickCalculator();

  const hasResults = state.rdwData !== null;
  const showError = state.error !== null && !state.isLoading;

  // Track the plate we've already looked up to avoid duplicate lookups
  const lastLookedUpPlateRef = useRef<string | null>(null);

  // Perform existing vehicle lookup after successful RDW calculation
  useEffect(() => {
    // Only lookup if we have RDW data and haven't looked up this plate yet
    if (!state.rdwData || !state.isOpen) return;

    const normalizedPlate = state.rdwData.plate;
    if (normalizedPlate === lastLookedUpPlateRef.current) return;

    // Mark as looked up immediately to prevent duplicate calls
    lastLookedUpPlateRef.current = normalizedPlate;

    // Fire lookup in background (non-blocking)
    lookupExistingVehicleWithTimeout(teamId, normalizedPlate, 3000)
      .then((result) => {
        // Only update if calculator is still open and plate hasn't changed
        if (state.isOpen) {
          setExistingVehicle(result);
        }
      })
      .catch(() => {
        // Swallow errors silently
      });
  }, [state.rdwData, state.isOpen, teamId, setExistingVehicle]);

  // Reset lookup tracking when calculator closes
  useEffect(() => {
    if (!state.isOpen) {
      lastLookedUpPlateRef.current = null;
    }
  }, [state.isOpen]);

  const handleCopy = () => {
    if (!state.rdwData) return;

    void copyCalculatorResult({
      rdwData: state.rdwData,
      bpm: state.bpm,
      classification: state.classification,
      maxBuyPrice: state.maxBuyPrice,
      expectedSellingPrice: state.expectedSellingPrice,
      targetMarginPct: state.targetMarginPct,
    });
  };

  // Navigate to Add Vehicle with prefill data
  const handleCreateVehicle = useCallback(() => {
    const prefill = buildCalculatorPrefill(state);
    if (!prefill) return;

    // Close calculator first
    close();

    // Navigate to vehicles page with prefill
    const url = buildPrefillUrl('/vehicles?action=add', prefill);
    navigate(url);
  }, [state, close, navigate]);

  // Navigate to Add Purchase Invoice with prefill data
  const handleCreatePurchaseInvoice = useCallback(() => {
    const prefill = buildCalculatorPrefill(state);
    if (!prefill) return;

    // Close calculator first
    close();

    // Navigate to purchase invoices page with prefill
    const url = buildPrefillUrl('/purchase-invoices?action=add', prefill);
    navigate(url);
  }, [state, close, navigate]);

  // Navigate to existing vehicle detail page
  const handleOpenExistingVehicle = useCallback(() => {
    const existing = state.existingVehicle;
    if (!existing || existing === 'UNKNOWN') return;

    // Close calculator first
    close();

    // Navigate to vehicle detail page
    navigate(`/vehicles/${existing.id}`);
  }, [state.existingVehicle, close, navigate]);

  // Determine which vehicle action to show
  const existingVehicle = state.existingVehicle;
  const hasExistingVehicle = existingVehicle !== null && existingVehicle !== 'UNKNOWN';

  return (
    <Dialog open={state.isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="w-full h-full max-h-[100dvh] sm:w-auto sm:h-auto sm:max-w-[480px] sm:max-h-[90vh] flex flex-col p-0 gap-0 sm:rounded-lg rounded-none"
        data-calculator-popover
      >
        {/* Header - with safe area padding on mobile */}
        <DialogHeader
          className="px-4 pt-4 sm:pt-4 pb-3 border-b border-border"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Calculator className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                {t('calculator.title', 'Snelle Calculator')}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {t('calculator.subtitle', 'BPM & prijsberekening op kenteken')}
              </DialogDescription>
            </div>
          </div>
          {/* Close button is provided by DialogContent component */}
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Plate input - always visible */}
          <PlateInput
            value={state.plate}
            onChange={setPlate}
            onSubmit={calculate}
            isLoading={state.isLoading}
            error={showError ? state.errorMessage : null}
            autoFocus
            retryDisabled={retryCountdown > 0}
            retryCountdown={retryCountdown}
          />

          {/* Error card */}
          {showError && state.error && (
            <CalculatorErrorCard
              error={state.error}
              errorMessage={state.errorMessage}
              onRetry={calculate}
              isLoading={state.isLoading}
            />
          )}

          {/* Results - only show when we have data */}
          {hasResults && state.rdwData && (
            <>
              {/* Vehicle summary */}
              <VehicleSummaryCard
                rdwData={state.rdwData}
                existingVehicle={existingVehicle}
                onOpenExistingVehicle={handleOpenExistingVehicle}
              />

              {/* Risk flags - only if there are active flags */}
              {state.riskFlags && hasActiveRiskFlags(state.riskFlags) && (
                <RiskFlagsCard riskFlags={state.riskFlags} />
              )}

              {/* BPM calculation */}
              {state.bpm && <BpmCard bpm={state.bpm} riskFlags={state.riskFlags} />}

              {/* Classification */}
              {state.classification && <ClassificationCard classification={state.classification} />}

              {/* Max buy price calculator */}
              <MaxBuyPriceCard
                expectedSellingPrice={state.expectedSellingPrice}
                targetMarginPct={state.targetMarginPct}
                restBpm={state.bpm?.restBpm ?? null}
                maxBuyPrice={state.maxBuyPrice}
                onSellingPriceChange={setExpectedSellingPrice}
                onMarginChange={setTargetMarginPct}
              />
            </>
          )}

          {/* Loading skeleton */}
          {state.isLoading && !hasResults && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-card p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-3 w-24 bg-muted rounded" />
                  </div>
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4 animate-pulse">
                <div className="space-y-3">
                  <div className="h-4 w-28 bg-muted rounded" />
                  <div className="h-3 w-full bg-muted rounded" />
                  <div className="h-3 w-3/4 bg-muted rounded" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions - with safe area padding on mobile */}
        <div
          className="px-4 pb-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <CalculatorActions
            hasResults={hasResults}
            isLoading={state.isLoading}
            hasExistingVehicle={hasExistingVehicle}
            onCreateVehicle={handleCreateVehicle}
            onOpenExistingVehicle={handleOpenExistingVehicle}
            onCreatePurchaseInvoice={handleCreatePurchaseInvoice}
            onCopy={handleCopy}
            onClose={close}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
